import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import {
  DEFAULT_AUTO_LOGOUT_MINUTES,
  normalizeAutoLogoutMinutes,
  type AutoLogoutMinutes,
} from "../domain/session-timeout";

const SETTINGS_KEY = "cloud4smart.view-settings";

type ViewSettings = {
  showPublicGateway: boolean;
  showPrivateGateway: boolean;
  showVncAddress: boolean;
  showGatewayIpv6: boolean;
  autoLogoutMinutes: AutoLogoutMinutes;
};

type SettingsContextValue = ViewSettings & {
  update: (settings: Partial<ViewSettings>) => void;
};

const defaults: ViewSettings = {
  showPublicGateway: false,
  showPrivateGateway: false,
  showVncAddress: false,
  showGatewayIpv6: false,
  autoLogoutMinutes: DEFAULT_AUTO_LOGOUT_MINUTES,
};
const SettingsContext = createContext<SettingsContextValue | null>(null);

function restore(): ViewSettings {
  try {
    const stored = JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? "{}") as Partial<ViewSettings>;
    return {
      ...defaults,
      ...stored,
      autoLogoutMinutes: normalizeAutoLogoutMinutes(stored.autoLogoutMinutes),
    };
  } catch {
    return defaults;
  }
}

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<ViewSettings>(restore);
  const value = useMemo<SettingsContextValue>(
    () => ({
      ...settings,
      update: (partial) => {
        setSettings((current) => {
          const next = { ...current, ...partial };
          localStorage.setItem(SETTINGS_KEY, JSON.stringify(next));
          return next;
        });
      },
    }),
    [settings],
  );
  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useViewSettings(): SettingsContextValue {
  const value = useContext(SettingsContext);
  if (!value) throw new Error("useViewSettings must be used inside SettingsProvider");
  return value;
}
